// src/services/authService.js
// Filepath: // src/services/authService.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../firebase"; // ✅ use initialized auth instance


// Normalize boolean-ish env
const envTrue = (v) => String(v).toLowerCase() === "true";
const USE_POPUP = envTrue(process.env.REACT_APP_AUTH_POPUP);

const postFormData = async (url, data) => {
  const response = await fetch(url, {
    method: "POST",
    body: data,
  });
  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }
  return await response.text();
};

// --- Third-party sync (exported so scenes can call it too)
export const handleThirdPartyAPICalls = async (user) => {
  if (!user) throw new Error("No user provided for third-party sync");

  // Use issuedAtTime to avoid repeating the sync for the same auth session
  const tokenResult = await user.getIdTokenResult();
  const token = tokenResult.token;
  const stampKey = `thirdPartySynced:v1:${user.uid}:${tokenResult.issuedAtTime}`;
  const alreadySynced = localStorage.getItem(stampKey) === "1";

  if (alreadySynced) {
    // Still keep the last authResponseData around from previous sync
    return;
  }

  const socialLoginJWTURL = process.env.REACT_APP_SOCIAL_LOGIN_JWT_URL;
  const socialAuthenticateURL = process.env.REACT_APP_SOCIAL_AUTHENTICATE_URL;

  // 1) Issue JWT to partner
  {
    const formData = new FormData();
    formData.append("fbtoken", token);
    formData.append("currentUser", JSON.stringify(user));
    await postFormData(socialLoginJWTURL, formData);
  }

  // 2) Authenticate and get partner session data
  {
    const authData = new FormData();
    authData.append("fbtoken", token);
    const authResponseData = await postFormData(socialAuthenticateURL, authData);
    localStorage.setItem("authResponseData", authResponseData);
  }

  // Mark synced for this token lifetime
  localStorage.setItem(stampKey, "1");
};

// ---------- EMAIL/PASSWORD ----------
export const signUpUser = async (email, password) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;
  await handleThirdPartyAPICalls(user);
  return user;
};

export const signInUserWithEmailPassword = async (email, password) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;
  await handleThirdPartyAPICalls(user);
  return user;
};

// ---------- GOOGLE / APPLE with popup-or-redirect switch ----------
export const startGoogleRedirect = async () => {
  const provider = new GoogleAuthProvider();
  if (USE_POPUP) {
    const { user } = await signInWithPopup(auth, provider);
    await handleThirdPartyAPICalls(user);
    return user;
  } else {
    await signInWithRedirect(auth, provider);
    return null;
  }
};

export const startAppleRedirect = async () => {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  if (USE_POPUP) {
    const { user } = await signInWithPopup(auth, provider);
    await handleThirdPartyAPICalls(user);
    return user;
  } else {
    await signInWithRedirect(auth, provider);
    return null;
  }
};

/**
 * Handle the redirect result once after the app reloads.
 * Returns the User on success, or null if no redirect happened.
 */
export const handleRedirectResultOnce = async () => {
  const result = await getRedirectResult(auth); // null if no redirect happened
  if (result?.user) {
    await handleThirdPartyAPICalls(result.user);
    return result.user;
  }
  return null;
};

// ---------- FOR SCENES: ensure sync for current user ----------
export const ensureThirdPartySyncForCurrentUser = async () => {
  const u = auth.currentUser;
  if (!u) return;
  await handleThirdPartyAPICalls(u);
};

// ---------- FRIENDLY ERROR + PASSWORD RESET ----------
export const getFriendlyErrorMessage = (errorCode) => {
  switch (errorCode) {
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/operation-not-allowed":
      return "Email/password accounts are not enabled.";
    case "auth/weak-password":
      return "Your password is too weak. Please use a stronger password.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";
    case "auth/user-not-found":
      return "No account found with this email address.";
    case "auth/wrong-password":
      return "Incorrect password. Please try again.";
    default:
      return "An unexpected error occurred. Please try again later.";
  }
};

export const sendPasswordResetEmail = async (email) => {
  return firebaseSendPasswordResetEmail(auth, email);
};

// ---------- SAFE SIGN OUT ----------
export const signOutSafely = async () => {
  try {
    await signOut(auth);
  } catch (e) {
    // no-op
  }
  try {
    localStorage.clear();
  } catch {}
  try {
    sessionStorage.clear();
  } catch {}
  try {
    if (indexedDB?.databases) {
      const dbs = await indexedDB.databases();
      await Promise.all(
        dbs.map(
          (db) =>
            db?.name &&
            new Promise((res) => {
              const req = indexedDB.deleteDatabase(db.name);
              req.onsuccess = req.onerror = req.onblocked = () => res();
            })
        )
      );
    } else {
      // Best-effort common Firebase DBs
      ["firebaseLocalStorageDb", "firebase-installations-database"].forEach((name) => {
        try {
          indexedDB.deleteDatabase(name);
        } catch {}
      });
    }
  } catch {}
};
